import React from 'react';
import { motion } from 'framer-motion';
import { WaveLogo } from '../shared';
import { MonoTag } from './shared';
import { ONBOARDING_STEPS } from './data';

const DOT = 38;
const GREEN = '#00B061';
const ORANGE = '#f08416';
const DIM_LINE = '#383838';
const PENDING = '#5a5a5a';
const MONO = 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace';

// Timeline sequencing (item 2): the connecting line moves first; the bubble glow
// ripple + the title grow/colour only kick in once the line has arrived.
const LINE_DUR = 0.4; // seconds
const POST_DELAY = 0.28; // bubble/title/ripple start as the line is finishing

// ── Progress bar (item 3): segmented ticks, each springs in ──────
const RailProgress: React.FC<{ step: number }> = ({ step }) => (
  <div
    style={{
      borderTop: '1px solid #383838',
      paddingTop: 16,
      marginLeft: -44,
      marginRight: -44,
      paddingLeft: 44,
      paddingRight: 44,
    }}
  >
    <MonoTag color="#787878">{step} of 5 complete</MonoTag>
    <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
      {ONBOARDING_STEPS.map((s, i) => (
        <div
          key={s.n}
          style={{
            flex: 1,
            height: 3,
            background: '#383838',
            position: 'relative',
          }}
        >
          <motion.div
            initial={false}
            animate={{ scaleX: i < step ? 1 : 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 30 }}
            style={{
              position: 'absolute',
              inset: 0,
              background: GREEN,
              transformOrigin: 'center',
            }}
          />
        </div>
      ))}
    </div>
  </div>
);

// ── One timeline row (item 2) ────────────────────────────────────
const TimelineNode: React.FC<{
  title: string;
  num: string;
  index: number;
  step: number;
  isLast: boolean;
}> = ({ title, num, index, step, isLast }) => {
  const done = index < step;
  const isActive = index === step;
  const accent = done ? GREEN : isActive ? ORANGE : PENDING;
  // Orange flows from the previous (done) bubble INTO the active bubble.
  const lineColor =
    index + 1 === step ? ORANGE : index + 1 < step ? GREEN : DIM_LINE;
  const isColored = lineColor !== DIM_LINE;

  // Bubble + title settle after the line has travelled (POST_DELAY).
  const bubbleTransition =
    `background-color .35s ease ${POST_DELAY}s,` +
    ` border-color .35s ease ${POST_DELAY}s,` +
    ` color .35s ease ${POST_DELAY}s, box-shadow .35s ease ${POST_DELAY}s`;
  const titleTransition = `color .35s ease ${POST_DELAY}s, font-size .3s ease ${POST_DELAY}s`;
  const statusTransition = `color .35s ease ${POST_DELAY}s`;

  return (
    <div style={{ display: 'flex', gap: 18, flex: 1, alignItems: 'stretch' }}>
      <div
        style={{
          width: DOT,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div style={{ position: 'relative', width: DOT, height: DOT }}>
          <div
            style={{
              boxSizing: 'border-box',
              position: 'relative',
              zIndex: 1,
              width: DOT,
              height: DOT,
              borderRadius: DOT,
              background: done ? GREEN : '#000',
              border: `2px solid ${accent}`,
              boxShadow: isActive
                ? '0 0 14px rgba(240,132,22,.7), 0 0 0 4px rgba(240,132,22,.12)'
                : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: done ? '#000' : accent,
              fontFamily: MONO,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '.04em',
              transition: bubbleTransition,
            }}
          >
            {done ? (
              <svg
                width="22"
                height="22"
                viewBox="0 0 16 16"
                style={{ display: 'block' }}
              >
                <path
                  d="M3 8 L7 12 L13 4"
                  fill="none"
                  stroke="#000"
                  strokeWidth={2}
                />
              </svg>
            ) : (
              num
            )}
          </div>
          {isActive && (
            <motion.span
              key={`ripple-${step}`}
              initial={{ scale: 1, opacity: 0.75 }}
              animate={{ scale: 2.3, opacity: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: POST_DELAY }}
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 0,
                borderRadius: DOT,
                border: `2px solid ${ORANGE}`,
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
        {!isLast && (
          <div
            style={{
              flex: 1,
              width: 2,
              marginTop: 4,
              marginBottom: 4,
              position: 'relative',
              background: DIM_LINE,
            }}
          >
            {/* Origin-top scaleY: fills downward going forward, recedes upward
                going back — naturally direction-aware. */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                background: isColored ? lineColor : 'transparent',
                transformOrigin: 'top',
                transform: `scaleY(${isColored ? 1 : 0})`,
                transition: `transform ${LINE_DUR}s ease, background-color .3s ease`,
              }}
            />
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ minHeight: DOT, display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              fontFamily: 'Bebas Neue, Kanit',
              fontSize: isActive ? 28 : 22,
              letterSpacing: '.03em',
              lineHeight: 1,
              position: 'relative',
              top: 2,
              color: isActive ? ORANGE : done ? '#b2afaf' : '#787878',
              transition: titleTransition,
            }}
          >
            {title}
          </div>
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '.22em',
            textTransform: 'uppercase',
            color: accent,
            fontFamily: MONO,
            transition: statusTransition,
          }}
        >
          {done ? '✓ complete' : isActive ? '● in progress' : '○ pending'}
        </div>
      </div>
    </div>
  );
};

const Rail: React.FC<{ step: number; width?: number }> = ({
  step,
  width = 420,
}) => (
  <div
    style={{
      width,
      flexShrink: 0,
      boxSizing: 'border-box',
      padding: '36px 44px',
      borderRight: '2px double rgba(255,255,255,.5)',
      display: 'flex',
      flexDirection: 'column',
      gap: 28,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 4,
      }}
    >
      <span
        style={{
          width: 11,
          height: 11,
          background: GREEN,
          boxShadow: '0 0 10px rgba(0,176,97,.7)',
          borderRadius: 1,
          transform: 'rotate(45deg)',
          flexShrink: 0,
        }}
      />
      <WaveLogo size={20} />
    </div>

    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {ONBOARDING_STEPS.map((s, i) => (
        <TimelineNode
          key={s.n}
          title={s.title}
          num={s.n}
          index={i}
          step={step}
          isLast={i === ONBOARDING_STEPS.length - 1}
        />
      ))}
    </div>

    <RailProgress step={step} />
  </div>
);

export default Rail;
